import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { logError } from '@core/Utils'
import { createImageBase64FromHtml, createImageBlobFromHtml } from './createImageFromHtml'
import { postShareBetDataAndGenerateLink } from '@core/Areas/ShareBet/helpers/api'
import {
  isBetSlipShareData,
  isKambiSportsShareData,
  isQuickbetShareData,
  isTicketShareData,
} from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/typeGuards'
import type { BetLoopShare, ShareBetData } from '@core/Areas/ShareBet/types'
import { getTicketStatus } from '@core/Areas/AccountActivity/Components/Ticket/helpers'
import { getKambiCouponBetStatus } from '@core/Areas/KambiSports/helpers/getKambiCouponBetStatus'
import { createTicketLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createTicketLoadBetDetails'
import { createKambiCouponLoadBetDetails } from '@core/Areas/ShareBet/LoadBet/createKambiCouponLoadBetDetails'
import type { LoadBetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import { createBetSlipLoadBetDetails } from './createBetSlipLoadBetDetails'
import { trackShareBetClick } from '../analytics'
import { ShareCopy } from './constants'
import { trackShareBetError, extractShareContext } from '@core/Areas/ShareBet/helpers/analytics'

export async function handleShareBetClick({
  data,
  previewEl,
  shouldAllowLink,
  isCostShared = false,
}: {
  data: Exclude<ShareBetData, BetLoopShare>
  shouldAllowLink: boolean
  previewEl?: HTMLDivElement | null
  isCostShared?: boolean
}) {
  try {
    if (!previewEl) throw Error('Unable to create share')

    const text: ShareBetText = { title: ShareCopy.Title, message: getShareMessage(data) }

    if (isReactNativeApp) {
      await shareWithReactNative({ data, text, previewEl, shouldAllowLink })
    } else {
      await shareWithBrowser({ data, text, previewEl, shouldAllowLink })
    }

    trackShareBetClick({ data, isCostShared, destination: 'External' })
  } catch (err) {
    const errorString: string | undefined = (err as Error).toString?.().toLowerCase()
    if (errorString && (errorString.includes('cancel') || errorString.includes('abort'))) return

    logError(err as Error, true)

    const { location, betType } = extractShareContext(data)
    trackShareBetError({
      message: 'Sorry, we are unable to create your share',
      destination: 'External',
      location,
      betType,
    })

    addToast({
      id: 'share-failed',
      timeoutMs: Infinity,
      icon: 'error',
      title: 'Sorry, we are unable to create your share',
    })
  }
}

// =============
// Local Helpers
// =============

function getShareMessage(data: ShareBetData): string {
  if (isTicketShareData(data)) {
    switch (getTicketStatus(data.share.ticket, data.share.activeTab)) {
      case 'Won':
        return ShareCopy.MessageWin
      case 'Pending':
        return ShareCopy.MessagePending
      case 'NoPayout':
        return ShareCopy.MessageNoPayout
      default:
        return ShareCopy.MessageDefault
    }
  }

  if (isBetSlipShareData(data)) {
    return data.share.isBetPlaced ? ShareCopy.MessagePending : ShareCopy.MessageUnplaced
  }

  if (isQuickbetShareData(data)) {
    return ShareCopy.MessagePending
  }

  if (isKambiSportsShareData(data)) {
    switch (getKambiCouponBetStatus(data.share.coupon.bets[0])) {
      case 'Won':
        return ShareCopy.MessageWin
      case 'NoPayout':
        return ShareCopy.MessageNoPayout
      default:
        return ShareCopy.MessagePending
    }
  }

  return ShareCopy.MessageUnplaced
}

async function createShareBetAndPost(data: Exclude<ShareBetData, BetLoopShare>) {
  let betsToShare: LoadBetDetails[] = []

  if (isTicketShareData(data)) {
    betsToShare = createTicketLoadBetDetails(data.share.ticket)
  } else if (isKambiSportsShareData(data)) {
    betsToShare = createKambiCouponLoadBetDetails(data.share.coupon)
  } else {
    const isMulti = data.share.items.length > 1
    betsToShare = data.share.items.flatMap(item => {
      const loadBetDetails = createBetSlipLoadBetDetails(item, isMulti)
      return loadBetDetails || []
    })
  }

  const response = await postShareBetDataAndGenerateLink(betsToShare)
  if (!response.link) throw Error('ShareBet: Error ' + response.message)

  return response
}

// ============
// Native Share
// ============

async function shareWithReactNative({
  data,
  text,
  shouldAllowLink,
  previewEl,
}: ShareBetWithShared) {
  const [{ link }, base64Image] = await Promise.all([
    shouldAllowLink ? createShareBetAndPost(data) : Promise.resolve({ link: null }),
    createImageBase64FromHtml(previewEl),
  ])

  const { title, message } = text
  sendToNative('SHARE_SHEET_REQUEST', {
    url: base64Image,
    ...(shouldAllowLink && { title, subject: title, message: message + link + ' 🤘' }),
  })
}

// =============
// Browser Share
// =============

async function shareWithBrowser({ data, text, shouldAllowLink, previewEl }: ShareBetWithShared) {
  const isImageOnly = !shouldAllowLink

  const [{ link }, imageBlob] = await Promise.all([
    isImageOnly ? Promise.resolve({ link: null }) : createShareBetAndPost(data),
    isImageOnly ? createImageBlobFromHtml(previewEl) : Promise.resolve(null),
  ])

  const { title, message } = text
  const navigatorShareData: ShareData =
    isImageOnly && imageBlob
      ? {
          files: [
            new File([imageBlob], `TABtouch-share-${Date.now()}.png`, { type: imageBlob.type }),
          ],
        }
      : { title, text: message + link + ' 🤘' }

  if (navigator.canShare?.(navigatorShareData)) {
    return await navigator.share(navigatorShareData)
  }
  throw Error('No capability to share')
}

// =====
// Types
// =====

interface ShareBetWithShared {
  data: Exclude<ShareBetData, BetLoopShare>
  text: ShareBetText
  shouldAllowLink: boolean
  previewEl: HTMLDivElement
}

interface ShareBetText {
  title: string
  message: string
}
